"use client";

import { useEffect, useState } from "react";
import { Clock, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const offices = [{
  code: "HQ",
  name: "INFINITYSECURE / HQ",
  address: "37 Woodloes St, Cannington WA 6107",
  description: "Our primary command center, orchestrating real-time AI threat detection and 24/7 security monitoring across all client networks.",
  timezone: "AWST",
  tzOffset: 8,
  cx: 27,
  cy: 38,
  cardPosition: "top-left",
  hours: "Mon-Fri 8:00 - 18:00",
  coverage: "Western Australia & National",
  lead: "Security Operations Lead"
}, {
  code: "SOC",
  name: "SECURITY / OPERATIONS",
  address: "37 Woodloes St, Cannington WA 6107",
  description: "Our dedicated Security Operations Center (SOC) provides continuous incident response and proactive risk management for our partners.",
  timezone: "AWST",
  tzOffset: 8,
  cx: 49,
  cy: 32,
  cardPosition: "bottom-right",
  hours: "24/7 Monitoring",
  coverage: "Global Threat Intelligence",
  lead: "Incident Response Team"
}, {
  code: "LAB",
  name: "CYBER / LABS",
  address: "37 Woodloes St, Cannington WA 6107",
  description: "Our research facility where we develop advanced AI models for phishing prevention and vulnerability assessment automation.",
  timezone: "AWST",
  tzOffset: 8,
  cx: 84,
  cy: 40,
  cardPosition: "top-right",
  hours: "Mon-Fri 9:00 - 17:00",
  coverage: "AI Security Innovation",
  lead: "Lead Security Researcher"
}];
function getOfficeTime(offsetHours: number) {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const local = new Date(utc + offsetHours * 3600000);
  const hh = local.getHours().toString().padStart(2, "0");
  const mm = local.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
}
export default function Location() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [times, setTimes] = useState(offices.map(o => getOfficeTime(o.tzOffset)));
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  useEffect(() => {
    const t = setInterval(() => {
      setTimes(offices.map(o => getOfficeTime(o.tzOffset)));
    }, 30000);
    return () => clearInterval(t);
  }, []);
  return <section data-section-id="5091" id="our-location" ref={ref} className="bg-background text-foreground py-24 md:py-40 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-24">
        <div className="lg:col-span-6 flex gap-5">
          <div className="w-px shrink-0 self-stretch" style={{
          backgroundColor: "var(--primary)"
        }} />
          <div className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="font-mono text-xs uppercase tracking-widest mb-6" style={{
            color: "var(--primary)"
          }}>
              [ Our Location ]
            </div>
            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-[64px] leading-[0.95] tracking-tight uppercase text-foreground">
              Secure
              <br />
              Operations.
            </h2>
          </div>
        </div>

        <div className="lg:col-span-6 flex flex-col justify-end gap-6">
          <p className={`font-default text-base md:text-[17px] leading-relaxed text-muted-foreground max-w-[420px] transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            InfinitySecure operates from our central hub in Cannington, providing 
            comprehensive cybersecurity protection and AI-driven threat intelligence 
            to businesses across the region and beyond.
          </p>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            <span style={{
            color: "var(--primary)"
          }}>///</span> Central Hub
            &nbsp;·&nbsp; 24/7 Security
          </div>
        </div>
      </div>

      <div className="max-w-[1480px] mx-auto hidden md:block">
        <div className="relative w-full border border-border" style={{
        aspectRatio: "21 / 9",
        backgroundColor: "hsl(0 0% 6%)",
        boxShadow: "var(--shadow-3xl)"
      }}>
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" style={{
          backgroundColor: "hsl(0 0% 4%)"
        }}>
            <g stroke="hsl(0 0% 10%)" strokeWidth="0.05">
              {[...Array(11)].map((_, i) => <line data-index={i} key={`v-${i}`} x1={i * 10} y1="0" x2={i * 10} y2="50" />)}
              {[...Array(6)].map((_, i) => <line data-index={i} key={`h-${i}`} x1="0" y1={i * 10} x2="100" y2={i * 10} />)}
            </g>

            <g fill="hsl(0 0% 10%)" stroke="hsl(0 0% 14%)" strokeWidth="0.1">
              <path d="M 8,18 L 14,14 L 22,12 L 30,14 L 34,18 L 32,24 L 28,30 L 24,34 L 20,32 L 16,28 L 12,24 Z" />
              <path d="M 26,34 L 30,36 L 32,42 L 30,48 L 26,46 L 24,40 Z" />
              <path d="M 44,18 L 50,16 L 54,18 L 52,22 L 48,24 L 44,22 Z" />
              <path d="M 46,26 L 52,26 L 56,32 L 54,40 L 50,44 L 46,40 L 44,32 Z" />
              <path d="M 54,14 L 64,12 L 74,14 L 82,16 L 86,20 L 84,26 L 78,28 L 70,26 L 62,24 L 56,22 Z" />
              <path d="M 76,30 L 82,30 L 84,34 L 80,36 L 76,34 Z" />
              <path d="M 82,38 L 90,38 L 92,42 L 88,44 L 82,42 Z" />
              <path d="M 36,10 L 42,8 L 44,12 L 40,14 L 36,12 Z" />
              <path d="M 84,22 L 86,20 L 86,24 L 84,26 Z" />
              <path d="M 46,18 L 48,17 L 48,21 L 46,21 Z" />
            </g>

            <g fill="none" stroke="var(--primary)" strokeWidth="0.15" strokeDasharray="0.6 0.6" opacity="0.55">
              <path d="M 27,38 Q 38,18 49,32" />
              <path d="M 49,32 Q 66,12 84,40" />
              <path d="M 27,38 Q 55,52 84,40" />
            </g>
          </svg>

          <div className="absolute inset-0">
            {offices.map((office, i) => {
            const isHovered = hoveredIndex === i;
            const cardClasses: Record<string, string> = {
              "top-left": "right-full bottom-full mr-6 mb-6 origin-bottom-right",
              "bottom-right": "left-full top-full ml-6 mt-6 origin-top-left",
              "top-right": "left-full bottom-full ml-6 mb-6 origin-bottom-left"
            };
            const lineClasses: Record<string, string> = {
              "top-left": "right-1/2 bottom-1/2 -translate-x-2 -translate-y-2 rotate-[-135deg] origin-bottom-right",
              "bottom-right": "left-1/2 top-1/2 translate-x-2 translate-y-2 rotate-[45deg] origin-top-left",
              "top-right": "left-1/2 bottom-1/2 translate-x-2 -translate-y-2 rotate-[-45deg] origin-bottom-left"
            };
            return <div data-index={i} key={office.code} className="absolute" style={{
              left: `${office.cx}%`,
              top: `${office.cy}%`
            }} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
                  <span className="absolute -translate-x-1/2 -translate-y-1/2 left-0 top-0 block w-2 h-2" aria-hidden>
                    <span className="absolute inset-0 animate-ping" style={{
                  backgroundColor: "var(--primary)",
                  opacity: isHovered ? 0.85 : 0.55
                }} />
                  </span>

                  <span className="absolute -translate-x-1/2 -translate-y-1/2 left-0 top-0 block w-2 h-2 z-10" style={{
                backgroundColor: "var(--primary)",
                boxShadow: isHovered ? "0 0 24px var(--primary), 0 0 6px var(--primary)" : "0 0 12px var(--primary)"
              }} />

                  <span className={`absolute h-px w-12 ${lineClasses[office.cardPosition]}`} style={{
                backgroundColor: isHovered ? "var(--primary)" : "hsl(0 0% 40%)"
              }} />

                  <div className={`absolute w-[260px] p-5 backdrop-blur-md border transition-all duration-300 ${cardClasses[office.cardPosition]}`} style={{
                backgroundColor: "hsl(0 0% 8% / 0.65)",
                borderColor: isHovered ? "var(--primary)" : "var(--border)",
                boxShadow: "var(--shadow-3xl)"
              }}>
                    <div className="font-default font-bold text-base tracking-tight uppercase mb-3 text-foreground">
                      {office.name}
                    </div>
                    <div className="font-mono text-[11px] uppercase tracking-wider leading-relaxed mb-3 text-muted-foreground">
                      {office.address}
                    </div>
                    <p className="font-default text-[13px] leading-snug text-foreground/80 mb-4 line-clamp-2">
                      {office.description}
                    </p>
                    <div className="flex items-center gap-2 font-mono text-[13px] pt-3 border-t" style={{
                  borderColor: "var(--border)"
                }}>
                      <span className="inline-block w-1.5 h-1.5" style={{
                    backgroundColor: "var(--primary)",
                    boxShadow: "0 0 6px var(--primary)"
                  }} />
                      <span className="uppercase tracking-wider" style={{
                    color: "var(--primary)"
                  }}>
                        Local Time: {times[i]} {office.timezone}
                      </span>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>

      <div className="md:hidden max-w-[1480px] mx-auto space-y-5">
        <div className="relative w-full border border-border" style={{
        aspectRatio: "16 / 10",
        backgroundColor: "hsl(0 0% 6%)",
        boxShadow: "var(--shadow-2xl)"
      }}>
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" style={{
          backgroundColor: "hsl(0 0% 4%)"
        }}>
            <g fill="hsl(0 0% 10%)" stroke="hsl(0 0% 14%)" strokeWidth="0.1">
              <path d="M 8,18 L 14,14 L 22,12 L 30,14 L 34,18 L 32,24 L 28,30 L 24,34 L 20,32 L 16,28 L 12,24 Z" />
              <path d="M 26,34 L 30,36 L 32,42 L 30,48 L 26,46 L 24,40 Z" />
              <path d="M 44,18 L 50,16 L 54,18 L 52,22 L 48,24 L 44,22 Z" />
              <path d="M 46,26 L 52,26 L 56,32 L 54,40 L 50,44 L 46,40 L 44,32 Z" />
              <path d="M 54,14 L 64,12 L 74,14 L 82,16 L 86,20 L 84,26 L 78,28 L 70,26 L 62,24 L 56,22 Z" />
              <path d="M 82,38 L 90,38 L 92,42 L 88,44 L 82,42 Z" />
            </g>
            <g fill="none" stroke="var(--primary)" strokeWidth="0.2" strokeDasharray="0.6 0.6" opacity="0.55">
              <path d="M 27,38 Q 38,18 49,32" />
              <path d="M 49,32 Q 66,12 84,40" />
            </g>
            {offices.map((o, i) => <g data-index={i} key={o.code}>
                <circle cx={o.cx} cy={o.cy} r="1.2" fill="var(--primary)" />
              </g>)}
          </svg>
        </div>

        {offices.map((office, i) => <div data-index={i} key={office.code} className="p-5 border backdrop-blur-md" style={{
        backgroundColor: "hsl(0 0% 8% / 0.65)",
        borderColor: "var(--border)",
        boxShadow: "var(--shadow-2xl)"
      }}>
            <div className="flex items-start justify-between mb-3 gap-3">
              <div className="font-default font-bold text-lg tracking-tight uppercase text-foreground">
                {office.name}
              </div>
              <span className="inline-block w-2 h-2 mt-2" style={{
            backgroundColor: "var(--primary)",
            boxShadow: "0 0 8px var(--primary)"
          }} />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-3">
              {office.address}
            </div>
            <p className="font-default text-sm leading-snug text-foreground/80 mb-4">
              {office.description}
            </p>
            <div className="flex items-center gap-2 font-mono text-[12px] pt-3 border-t uppercase tracking-wider" style={{
          borderColor: "var(--border)",
          color: "var(--primary)"
        }}>
              <Clock className="size-3" />
              Local Time: {times[i]} {office.timezone}
            </div>
          </div>)}
      </div>

      <div className="max-w-[1480px] mx-auto mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-px border border-border bg-border">
        {offices.map((office, i) => <div data-index={i} key={office.code} className="p-8 md:p-10 bg-background">
            <div className="font-mono text-[11px] uppercase tracking-widest mb-6 flex items-center gap-2" style={{
          color: "var(--primary)"
        }}>
              <MapPin className="size-3" />
              {office.code} / Office Profile
            </div>
            <div className="space-y-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  Operating Hours
                </div>
                <div className="font-default text-base font-medium text-foreground">
                  {office.hours}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  Regional Coverage
                </div>
                <div className="font-default text-base font-medium text-foreground">
                  {office.coverage}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  Team Lead
                </div>
                <div className="font-default text-base font-medium text-foreground">
                  {office.lead}
                </div>
              </div>
            </div>
          </div>)}
      </div>
    </section>;
}